/* Pre-order buy box: swap the batch pane and relabel the buy button when the
 * selected variant changes.
 *
 * The theme does not re-render the product section on variant change — it
 * dispatches a bubbling `theme:variant:change` — so every variant's pane is
 * rendered server-side and swapped here.
 *
 * Relabelling is needed because the theme's own pre-order support keys off a
 * single `[data-product-preorder]` marker cached at init, which is product-wide.
 * On a product where only some variants are armed that would label every
 * variant. Liquid marks each pane with data-po-active instead, and this reads
 * it. `pushState` calls updateAddToCartState before fireHookEvent, so the
 * theme has already written its label by the time this runs.
 *
 * Read-only with respect to the app: never writes preorder metafields, never
 * touches availability. A variant whose `available` is false keeps the
 * sold-out label — the app closes intake through inventory policy. On this
 * Horizon port there is no theme-native step that writes that label first
 * (the Broadcast original's premise for the early return below); it is
 * afroyla-material-cards.js's updateAddToCartButton that owns "Sold out" now
 * (code review finding H2) — this module still defers to it by staying
 * hands-off whenever `variant.available === false`, same as before. */
(function () {
  'use strict';

  var PANE = '.apo-pane';
  var ATC_TEXT = '[data-add-to-cart-text]';

  /* Walk up to the nearest ancestor that actually holds panes. A quick-add
   * modal opened over a product page puts a second buy box on the same
   * document, and scoping to `document` would relabel both buttons from
   * whichever one changed. */
  function scopeOf(node) {
    for (var el = node; el && el.nodeType === 1; el = el.parentElement) {
      if (el.querySelector && el.querySelector(PANE)) return el;
    }
    return document;
  }

  function strings() {
    var s = (window.theme && window.theme.strings) || {};
    return { pre: s.preOrder || 'Pre-order', add: s.addToCart || 'Add to cart' };
  }

  /* hc-product-variant-picker-v2 recomputes the label from the button's own
   * data-preorder / data-preorder-text, which Liquid stamps once for the
   * server-selected variant and which therefore goes stale on a switch. It
   * runs after this handler, so writing the text alone would be overwritten a
   * frame later. Keep those attributes in sync with the selected variant and
   * both code paths agree on the same answer. */
  function markButton(scope, isPre, verb) {
    var btns = scope.querySelectorAll('[data-add-to-cart], button[name="add"]');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (isPre) {
        btn.dataset.preorder = 'true';
        btn.dataset.preorderText = verb;
      } else {
        delete btn.dataset.preorder;
        delete btn.dataset.preorderText;
      }
    }
  }

  /* Swap only the verb, keeping the price suffix the theme appends when
   * atcButtonShowPrice is on. */
  function relabel(scope, verb) {
    var nodes = scope.querySelectorAll(ATC_TEXT);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var price = el.querySelector('.btn__price');
      if (price) {
        el.textContent = verb + ' ';
        el.appendChild(price);
      } else {
        el.textContent = verb;
      }
    }
  }

  function apply(scope, variant) {
    // No pre-order panes at all -> this module has nothing to relabel, ever.
    // afroyla-material-cards.js owns the plain Add to cart / Sold out verb
    // outright on products like this (H2 precedence: preorder owns the verb
    // only when panes exist).
    var panes = scope.querySelectorAll(PANE);
    if (!panes.length) return;

    var wanted = String((variant && variant.id) || '');
    var active = null;

    for (var i = 0; i < panes.length; i++) {
      var pane = panes[i];
      if (pane.getAttribute('data-po-variant') === wanted) {
        pane.removeAttribute('hidden');
        active = pane;
      } else {
        pane.setAttribute('hidden', '');
      }
    }

    // No variant means an unavailable combination; leave the label alone.
    if (!variant) return;
    // Sold out stays sold out — afroyla-material-cards.js owns that label.
    if (variant.available === false) return;

    var isPre = active && active.getAttribute('data-po-active') === 'true';
    // A Simple Bundles variant whose components mix in-stock and pre-order
    // pieces checks out with the plain verb (owner rule 29.07 for mixed
    // sets): the "Ships in two parts" manifest above the button owns the
    // story. Liquid stamps data-po-mixed from afroyla-preorder-facts.
    if (isPre && active.getAttribute('data-po-mixed') === 'true') isPre = false;
    var verb = isPre ? active.getAttribute('data-po-button') || strings().pre : strings().add;
    markButton(scope, isPre, verb);
    relabel(scope, verb);
  }

  document.addEventListener('theme:variant:change', function (event) {
    apply(scopeOf(event.target), event.detail && event.detail.variant);
  });

  /* Owner decision 29.07: a ticked set that contains ANY in-stock piece
   * checks out with the plain add verb — the ship manifest right above the
   * button owns the mixed-ship story. All-pre-order sets keep the reserve
   * verb; unticking back to solo restores the anchor's own verb. Deferred a
   * tick so it lands after the theme's variant relabel when both fire in
   * the same frame. Attributes (markButton) stay anchor-owned on purpose:
   * the variant picker's recompute keeps agreeing with the anchor, and this
   * listener re-fires right after via the picker's own variant handler. */
  document.addEventListener('afroyla:set:change', function (event) {
    var detail = event.detail || {};
    if (!detail.picker) return;
    var scope = scopeOf(detail.picker);
    if (!scope) return;

    window.setTimeout(function () {
      var active = null;
      var panes = scope.querySelectorAll(PANE);
      for (var i = 0; i < panes.length; i++) {
        if (panes[i].getAttribute('data-po-active') === 'true') active = panes[i];
      }
      var anchorPre = !!(detail.anchor && detail.anchor.pre);
      var preVerb = (active && active.getAttribute('data-po-button')) || strings().pre;

      if (detail.kind !== 'set') {
        relabel(scope, anchorPre ? preVerb : strings().add);
        return;
      }
      var items = [detail.anchor].concat(detail.members || []);
      var hasInstock = items.some(function (it) { return it && !it.pre; });
      relabel(scope, hasInstock ? strings().add : preVerb);
    }, 0);
  });
})();
